import React from "react";

import SizeSelector from "../size-selector";

import styles from "../size-selector/styles/size-selector.module.scss";

export const BRA_LABEL = "Cup Size";
export const EBB_SIZE_LABEL = "Size";
export const INSEAM_LABEL = "Length";

/**
 * @typedef {Object} VariantSelectorProps
 * @property {string[]} allSizes - the sizes/variants to display
 * @property {boolean} isMobile - whether the user is on a mobile device
 * @property {"Size"|"Cup Size"|"Length"} label - the label to display above the selector
 * @property {string} selectedVariant - the currently selected variant
 * @property {React.ReactNode} hint - hint to display below the selector
 * @property {function} onSelection - the function to call when a variant is selected
 */

/**
 * A variant selector component that displays a list of sizes/lenghts/cup sizes
 *
 * @param {VariantSelectorProps} props
 * @returns {React.ReactElement}
 *
 * @example
 *  <VariantSelector
 *    allSizes={["12\"", "14\"", "16\""]}
 *    isMobile={false}
 *    label={"Length"}
 *    onSelection={() => {}}
 *    selectedVariant={"12\""}
 *    hint={!showAbcPantsVariations ? <ConnectedFitDetails /> : null}
 *  />
 */
export function VariantSelector({
  allSizes = [],
  getResizeSubscriber,
  hint = null,
  isMobile = false,
  label,
  onSelection,
  selectedVariant
}) {
  return (
    !!allSizes.length && (
      <div className={styles.variantSelectorWrapper}>
        <SizeSelector
          data-testid="variant-selector"
          allSizes={allSizes}
          availableSizes={allSizes}
          isMobile={isMobile}
          activeSize={selectedVariant}
          primaryLabel={{ label, value: selectedVariant }}
          onSelectCallback={onSelection}
          hint={hint}
          getResizeSubscriber={getResizeSubscriber}
        />
      </div>
    )
  );
}

/**
 * Variant Selector for Ebb Sizes - e.g. 1L, 2L, Mini
 *
 * @param {VariantSelectorProps} props - the component props
 * @param {function} props.useSetOneSize - useSetOneSize hook forces ONE SIZE to always be selected
 * @param {function} props.useVariantPropsHook - useVariantPropsHook hook returns the relevant props for the variant selector based on the label
 *
 * @returns {React.ReactElement}
 */
export const EbbSizeVariantSelector = ({
  useSetOneSize,
  useVariantPropsHook,
  ...otherProps
}) => {
  useSetOneSize?.();
  const props = useVariantPropsHook?.(EBB_SIZE_LABEL) ?? otherProps;
  return <VariantSelector {...props} label={EBB_SIZE_LABEL} />;
};

/**
 * Variant Selector for Bra Cup Sizes - e.g. 32A, 34B, 36C
 *
 * @param {VariantSelectorProps} props - the component props
 * @param {function} props.useVariantPropsHook - useVariantPropsHook hook returns the relevant props for the variant selector based on the label
 *
 * @returns {React.ReactElement}
 */
export const BraVariantSelector = ({ useVariantPropsHook, ...otherProps }) => {
  const props = useVariantPropsHook?.(BRA_LABEL) ?? otherProps;
  return <VariantSelector {...props} label={BRA_LABEL} />;
};

/**
 * Variant Selector for Inseam Lengths - e.g. 21", 24", 32"
 *
 * @param {VariantSelectorProps} props - the component props
 * @param {function} props.useVariantPropsHook - useVariantPropsHook hook returns the relevant props for the variant selector based on the label
 *
 * @returns {React.ReactElement}
 */
export const InseamVariantSelector = ({
  useVariantPropsHook,
  ...otherProps
}) => {
  const props = useVariantPropsHook?.(INSEAM_LABEL) ?? otherProps;
  return <VariantSelector {...props} label={INSEAM_LABEL} />;
};
